import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { formatPrice, resolveHeroImage, NoImagePlaceholder } from "@/components/ProjectCard";
import {
  ArrowRight, Building2, ChevronRight, IndianRupee, Layers,
  Loader2, MapPin, Search, ShieldCheck,
} from "lucide-react";

// Same head-injection helper spec as LocalityLanding — kept lightweight
// so we don't force a react-helmet dependency.
const OWN_TAG_MARK = "data-inprop-seo";

function useSeoHead({ title, description, canonical, ogImage, jsonLd }) {
  useEffect(() => {
    const created = [];
    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute(OWN_TAG_MARK, "1");
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value);
    };
    const upsertLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute(OWN_TAG_MARK, "1");
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("href", href);
    };
    const originalTitle = document.title;
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);
    upsertLink("canonical", canonical);
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute(OWN_TAG_MARK, "1");
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }
    return () => {
      document.title = originalTitle;
      created.forEach((el) => el.remove());
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, JSON.stringify(jsonLd)]);
}

function absoluteUrl(pathname) {
  const canonicalOrigin =
    process.env.REACT_APP_CANONICAL_ORIGIN || "https://inpropportal.com";
  return `${canonicalOrigin.replace(/\/$/, "")}${pathname}`;
}

const STATUS_LABEL = {
  READY_TO_MOVE: "Ready to Move",
  UNDER_CONSTRUCTION: "Under Construction",
  PRE_LAUNCH: "Pre-Launch",
  RESALE: "Resale",
};

function DeveloperProjectCard({ p, developerName }) {
  const hero = resolveHeroImage(p);
  const priceLabel = p.price_lakhs ? formatPrice(p.price_lakhs) : "Price on request";
  return (
    <Link
      to={`/projects/${p.project_id}`}
      className="sg-card overflow-hidden flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all"
      data-testid={`developer-project-${p.project_id}`}
    >
      <div className="aspect-[16/10] bg-sg-cream-soft relative overflow-hidden">
        {hero ? <img src={hero} alt={p.project_name} className="w-full h-full object-cover" /> : <NoImagePlaceholder />}
        {p.construction_status ? (
          <div className="absolute top-3 left-3 chip chip-gold text-[10px] uppercase">{STATUS_LABEL[p.construction_status] || p.construction_status}</div>
        ) : null}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-sg-mute font-semibold">{developerName}</div>
        <div className="font-display text-lg text-sg-navy mt-1 leading-tight">{p.project_name}</div>
        <div className="text-xs text-sg-mute mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.locality} · {p.zone}</div>
        {p.configurations?.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {p.configurations.slice(0, 4).map((c) => (
              <span key={c} className="chip text-[10px]">{c}</span>
            ))}
          </div>
        ) : null}
        <div className="mt-auto pt-3 flex items-baseline justify-between border-t border-sg-line/60 mt-3">
          <div className="font-mono text-sm text-sg-gold-deep">{priceLabel}</div>
          <span className="text-xs font-semibold text-sg-navy inline-flex items-center gap-1">View <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function DeveloperLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/developer-landing/${slug}`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const seoTitle = data ? `${data.name} Projects in Pune · INPropPortal` : "Developer · INPropPortal";
  const seoDesc = data?.meta_description || "Verified developer projects across Pune on INPropPortal.";
  const canonical = data ? absoluteUrl(`/developer/${data.slug}`) : undefined;

  const jsonLd = useMemo(() => {
    if (!data) return null;
    const flat = (data.locality_groups || []).flatMap((g) => g.projects || []);
    const items = flat.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": absoluteUrl(`/projects/${p.project_id}`),
      "name": p.project_name,
    }));
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": absoluteUrl("/") },
          { "@type": "ListItem", "position": 2, "name": "Pune", "item": absoluteUrl("/buy") },
          { "@type": "ListItem", "position": 3, "name": data.name, "item": canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": data.name,
        "url": canonical,
        "areaServed": "Pune",
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `${data.name} Pune projects`,
        "numberOfItems": data.total_projects,
        "itemListElement": items,
      },
      ...(data.editorial
        ? [{
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${data.name} in Pune — Developer Profile`,
            "articleBody": data.editorial,
            "about": data.name,
            "author": { "@type": "Organization", "name": "INPropPortal Pune Desk" },
            "publisher": { "@type": "Organization", "name": "INPropPortal", "url": absoluteUrl("/") },
            "mainEntityOfPage": canonical,
          }]
        : []),
    ];
  }, [data, canonical]);

  useSeoHead({
    title: seoTitle,
    description: seoDesc,
    canonical,
    ogImage: "https://inpropportal.com/og-default.jpg",
    jsonLd,
  });

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[70vh] flex items-center justify-center text-sg-mute" data-testid="developer-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading developer…
        </div>
      </PublicLayout>
    );
  }
  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="developer-notfound">
          <div className="text-center max-w-md">
            <Building2 className="w-12 h-12 mx-auto text-sg-gold-deep" />
            <h1 className="font-display text-4xl text-sg-navy mt-4">Developer not on our platform yet.</h1>
            <p className="text-sm text-sg-mute mt-2">
              We only publish MahaCRITI-verified projects. Browse all Pune inventory or check back soon.
            </p>
            <button onClick={() => navigate("/buy")} className="pill-solid text-sm mt-5">Browse all listings</button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const priceBand = data.price_min_lakhs && data.price_max_lakhs
    ? (data.price_min_lakhs === data.price_max_lakhs
        ? formatPrice(data.price_min_lakhs)
        : `${formatPrice(data.price_min_lakhs)} – ${formatPrice(data.price_max_lakhs)}`)
    : "—";

  return (
    <PublicLayout>
      {/* HERO ─────────────────────────────────────────────────── */}
      <section className="bg-sg-navy text-white relative overflow-hidden" data-testid="developer-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-15"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/27826399/pexels-photo-27826399.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <nav className="text-xs text-white/60 mb-4" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3 inline mx-1" />
            <Link to="/buy" className="hover:text-white">Pune</Link>
            <ChevronRight className="w-3 h-3 inline mx-1" />
            <span className="text-white/90">{data.name}</span>
          </nav>
          <div className="overline text-sg-gold mb-3">VERIFIED DEVELOPER</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" data-testid="developer-h1">
            {data.name} <span className="font-serif-italic text-sg-gold">in Pune.</span>
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-base" data-testid="developer-lede">
            {data.meta_description}
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Projects</div>
              <div className="font-hero text-3xl mt-1">{data.total_projects}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Pune corridors</div>
              <div className="font-hero text-3xl mt-1">{data.total_localities}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Price band</div>
              <div className="font-hero text-lg mt-1 text-sg-gold whitespace-nowrap">{priceBand}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Verification</div>
              <div className="font-hero text-lg mt-1 inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-sg-gold" /> MahaCRITI</div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/buy?developer=${encodeURIComponent(data.name)}`} className="pill-solid inline-flex items-center gap-2" data-testid="developer-cta-search">
              <Search className="w-4 h-4" /> Search {data.name} inventory
            </Link>
          </div>
        </div>
      </section>

      {/* EDITORIAL ─────────────────────────────────────────────── */}
      {data.editorial && (
        <section className="max-w-4xl mx-auto px-6 lg:px-8 py-12" data-testid="developer-editorial">
          <div className="overline text-sg-gold-deep mb-3">DEVELOPER PROFILE · {data.name.toUpperCase()}</div>
          <article className="font-hero text-2xl md:text-[28px] leading-[1.35] text-sg-navy">
            {data.editorial}
          </article>
          <div className="mt-4 text-xs font-mono text-sg-mute uppercase tracking-wider">
            Written by INPropPortal&apos;s Pune desk · updated for Q1 2026
          </div>
        </section>
      )}

      {/* PROJECTS BY LOCALITY ─────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="mb-8">
          <div className="overline text-sg-gold-deep">INVENTORY BY CORRIDOR</div>
          <h2 className="font-display text-3xl text-sg-navy mt-1">
            {data.total_projects} verified {data.total_projects === 1 ? "project" : "projects"} across
            <span className="font-serif-italic text-sg-gold-deep"> {data.total_localities} Pune corridor{data.total_localities !== 1 ? "s" : ""}.</span>
          </h2>
        </div>

        {data.locality_groups.length === 0 ? (
          <div className="sg-card-cream p-10 text-center max-w-2xl mx-auto">
            <Building2 className="w-10 h-10 mx-auto text-sg-gold-deep" />
            <h3 className="font-display text-2xl text-sg-navy mt-3">No live projects right now.</h3>
            <p className="text-sm text-sg-mute mt-2">
              {data.name} inventory refreshes as agents publish new listings. Check back soon.
            </p>
          </div>
        ) : (
          <div className="space-y-12" data-testid="developer-groups">
            {data.locality_groups.map((g) => (
              <div key={g.locality} data-testid={`developer-group-${g.locality_slug}`}>
                <div className="flex items-baseline justify-between gap-4 mb-4 border-b border-sg-line pb-3">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-display text-2xl text-sg-navy">
                      {data.name} in {g.locality}
                    </h3>
                    <span className="chip text-[11px]">
                      <Layers className="w-3 h-3 mr-1 text-sg-gold-deep" />
                      {g.count} project{g.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <Link to={`/pune/${g.locality_slug}`} className="text-xs text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1 whitespace-nowrap">
                    Explore {g.locality} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {g.projects.map((p) => (
                    <DeveloperProjectCard key={p.project_id} p={p} developerName={data.name} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* RELATED DEVELOPERS ─────────────────────────────────── */}
      {data.related_developers?.length > 0 && (
        <section className="bg-sg-cream-soft/70 border-t border-sg-line py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="overline text-sg-gold-deep mb-2">RELATED DEVELOPERS</div>
            <h2 className="font-display text-3xl text-sg-navy">Other Pune builders on INPropPortal.</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="developer-related-grid">
              {data.related_developers.map((d) => (
                <Link
                  key={d.slug}
                  to={`/developer/${d.slug}`}
                  className="bg-white border border-sg-line rounded-2xl p-4 hover:border-sg-gold-deep transition-colors"
                  data-testid={`developer-related-${d.slug}`}
                >
                  <div className="font-display text-lg text-sg-navy leading-tight">{d.name}</div>
                  <div className="text-[11px] text-sg-mute mt-1">
                    {d.project_count} project{d.project_count !== 1 ? "s" : ""} · {(d.localities || []).length} corridor{(d.localities || []).length !== 1 ? "s" : ""}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA ─────────────────────────────────────────── */}
      <section className="bg-sg-navy text-white py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="overline text-sg-gold mb-3">READY?</div>
          <h2 className="font-display text-4xl lg:text-5xl">
            Search live <span className="font-serif-italic text-sg-gold">{data.name}</span> inventory.
          </h2>
          <div className="mt-8 inline-flex gap-3 flex-wrap justify-center">
            <Link to={`/buy?developer=${encodeURIComponent(data.name)}`} className="pill-solid inline-flex items-center gap-2">
              <Search className="w-4 h-4" /> Open search
            </Link>
          </div>
          <p className="text-xs text-white/50 mt-4">
            <IndianRupee className="w-3 h-3 inline -mt-0.5" /> All prices in Indian rupees · MahaCRITI-verified inventory
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
